import { useCallback, useState } from 'react'
import Group from 'components/utils/Group'
import Sheet from 'components/utils/Sheet'
import BodyText from 'LuxKit/Typography/BodyText'
import Image from 'components/Common/Image'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import styled from 'styled-components'
import { rem } from 'polished'
import { mediaQueryUp } from 'components/utils/breakpoint'
import srOnly from 'styles/tools/sr-only'
import LeLiveRegistrationForm from './LeLiveRegistrationForm'
import LeLiveNav from './LeLiveNav'
import type { RegistrationFormValues } from './LeLiveRegistrationForm'
import type { LeLiveRegistration } from 'leLive/api/leLive'
import { useIntl } from 'react-intl'

const SrOnlyH1 = styled.h1`${srOnly}`

const MobileOnlyLogo = styled.div`
  display: flex;
  justify-content: center;

  ${mediaQueryUp.desktop} {
    display: none;
  }
`

const HeroWrapper = styled.div`
  position: relative;
  background-color: #1a1a1a;
  overflow: hidden;
  opacity: 0;
  transition: opacity 0.3s ease-in;

  &.ready {
    opacity: 1;
  }
`

const HeroBackground = styled.div`
  position: absolute;
  inset: 0;
  z-index: 0;

  picture,
  > * {
    width: 100%;
    height: 100%;
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
  }
`

const HeroGradient = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    to bottom,
    rgba(0, 0, 0, 0.4) 0%,
    rgba(0, 0, 0, 0) 39%
  );
`

const HeroContent = styled(Sheet)`
  position: relative;
  z-index: 1;

  @media (min-width: ${rem(1400)}) {
    padding-block: ${rem(120)} ${rem(124)};
    padding-inline: ${rem(150)};
  }
`

const LeftContent = styled(Group)`
  min-width: 0;

  ${mediaQueryUp.desktop} {
    max-width: ${rem(633)};
  }
`

const TitleImage = styled.div`
  width: 100%;
  max-width: ${rem(311)};

  ${mediaQueryUp.desktop} {
    max-width: ${rem(633)};
  }

  img {
    width: 100%;
    height: auto;
  }
`

const HighlightPill = styled(Sheet)`
  border: 2px solid white;
  white-space: nowrap;
  flex-shrink: 0;
  margin-inline-end: ${rem(16)};
  color: #F3F1EA;

  ${mediaQueryUp.desktop} {
    margin-inline-end: 0;

    &[aria-hidden] {
      display: none;
    }
  }
`

const MarqueeTrack = styled.div`
  overflow: hidden;
  width: 100%;

  ${mediaQueryUp.desktop} {
    overflow: visible;
  }
`

const MarqueeInner = styled.div`
  display: flex;
  width: max-content;
  animation: marquee-scroll 20s linear infinite;
  --marquee-scroll-offset: -50%;

  &:dir(rtl) {
    --marquee-scroll-offset: 50%;
  }

  @keyframes marquee-scroll {
    from { transform: translateX(0); }
    to { transform: translateX(var(--marquee-scroll-offset)); }
  }

  ${mediaQueryUp.desktop} {
    width: auto;
    animation: none;
    flex-wrap: wrap;
    justify-content: center;
    gap: ${rem(16)};
  }
`

const FormWrapper = styled(Group)`
  max-width: ${rem(420)};
`

interface Props {
  isSubmitting: boolean
  isLoadingRegistrations: boolean
  registration: LeLiveRegistration | undefined
  submitError: string | undefined
  onRegistrationSubmit: (values: RegistrationFormValues) => Promise<void> | void
}

function LeLiveHero(props: Props) {
  const intl = useIntl()
  const { isSubmitting, isLoadingRegistrations, registration, submitError, onRegistrationSubmit } = props
  const leLiveEventName = intl.formatMessage({
    id: '57DuIElM_duJFpbWtRBd',
    defaultMessage: 'Luxury Escapes Live 2026',
    description: 'Accessible event name used for LE Live 2026 logo imagery and hero heading',
  })
  const [heroReady, setHeroReady] = useState(false)

  const handleBackgroundLoad = useCallback(() => {
    setHeroReady(true)
  }, [])

  const handleBackgroundError = useCallback(() => {
    setHeroReady(true)
  }, [])

  return (
    <HeroWrapper id="hero" className={heroReady ? 'ready' : undefined}>
      {!heroReady && <LoadingIndicator floating />}
      <HeroBackground>
        <Image
          id="egpqrc4p9s3oyi84eju6"
          alt=""
          width={1440}
          height={680}
          dpr={1}
          enhancedFormats
          fetchPriority="high"
          onLoad={handleBackgroundLoad}
          onError={handleBackgroundError}
        />
        <HeroGradient />
      </HeroBackground>

      <LeLiveNav hasRegistration={isLoadingRegistrations || !!registration} />
      <SrOnlyH1>{leLiveEventName}</SrOnlyH1>

      <HeroContent
        padding="40 20 80 20"
        desktopPadding="120 60 124 60"
      >
        <Group direction="horizontal" wrap="wrap" verticalAlign="center" horizontalAlign="center" gap={40}>
          <LeftContent
            direction="vertical"
            horizontalAlign="center"
            gap={24}
            grow={1}
          >
            <MobileOnlyLogo>
              <Image
                id="wbtblfr8vbl42dpjkpd"
                alt={leLiveEventName}
                width={160}
                height={80}
                dpr={2}
                enhancedFormats
              />
            </MobileOnlyLogo>
            <TitleImage>
              <Image
                id="gd3y38bcbbkvr4kdgi7b"
                alt={intl.formatMessage({
                  id: 'X5z906y402oH8mcStf7s',
                  defaultMessage: 'More than a travel expo',
                  description: 'Alt text for the LE Live event tagline image on the hero section',
                })}
                width={633}
                height={186}
                dpr={2}
                enhancedFormats
                fetchPriority="high"
              />
            </TitleImage>

            <MarqueeTrack>
              <MarqueeInner>
                <HighlightPill borderRadius="round" padding="8 20">
                  <BodyText variant="large" weight="bold">
                    {intl.formatMessage({
                      id: 'OgFOA9--QY1exh10btjH',
                      defaultMessage: 'Thousands in prizes & giveaways',
                      description: 'Scrolling marquee pill text highlighting thousands in prizes at LE Live event',
                    })}
                  </BodyText>
                </HighlightPill>
                <HighlightPill borderRadius="round" padding="8 20">
                  <BodyText variant="large" weight="bold">
                    {intl.formatMessage({
                      id: 'CNxapBW1nqak8v5rcBeT',
                      defaultMessage: 'Exclusive event-only offers',
                      description: 'Scrolling marquee pill text highlighting exclusive event-only offers at LE Live',
                    })}
                  </BodyText>
                </HighlightPill>
                <HighlightPill borderRadius="round" padding="8 20">
                  <BodyText variant="large" weight="bold">
                    {intl.formatMessage({
                      id: '80itDmm6C3hv-2qs1qUk',
                      defaultMessage: '50+ local & international exhibitors',
                      description: 'Scrolling marquee pill text highlighting the number of exhibitors at LE Live event',
                    })}
                  </BodyText>
                </HighlightPill>
                <HighlightPill borderRadius="round" padding="8 20" aria-hidden>
                  <BodyText variant="large" weight="bold">
                    {intl.formatMessage({
                      id: 'OgFOA9--QY1exh10btjH',
                      defaultMessage: 'Thousands in prizes & giveaways',
                      description: 'Scrolling marquee pill text highlighting thousands in prizes at LE Live event',
                    })}
                  </BodyText>
                </HighlightPill>
                <HighlightPill borderRadius="round" padding="8 20" aria-hidden>
                  <BodyText variant="large" weight="bold">
                    {intl.formatMessage({
                      id: 'CNxapBW1nqak8v5rcBeT',
                      defaultMessage: 'Exclusive event-only offers',
                      description: 'Scrolling marquee pill text highlighting exclusive event-only offers at LE Live',
                    })}
                  </BodyText>
                </HighlightPill>
                <HighlightPill borderRadius="round" padding="8 20" aria-hidden>
                  <BodyText variant="large" weight="bold">
                    {intl.formatMessage({
                      id: '80itDmm6C3hv-2qs1qUk',
                      defaultMessage: '50+ local & international exhibitors',
                      description: 'Scrolling marquee pill text highlighting the number of exhibitors at LE Live event',
                    })}
                  </BodyText>
                </HighlightPill>
              </MarqueeInner>
            </MarqueeTrack>
          </LeftContent>

          <FormWrapper direction="vertical" shrink={false} grow={1}>
            <LeLiveRegistrationForm
              onSubmit={onRegistrationSubmit}
              isSubmitting={isSubmitting}
              isLoading={isLoadingRegistrations}
              registration={registration}
              error={submitError}
            />
          </FormWrapper>
        </Group>
      </HeroContent>
    </HeroWrapper>
  )
}

export default LeLiveHero
